
import { useState, useCallback, useEffect } from "react";
import { Node, Edge, NodeMouseHandler, EdgeMouseHandler } from "@xyflow/react";
import { NodeData } from "../initial-nodes";

type ContextTargetType = "node" | "edge" | "group" | "text" | "actor" | "image" | null;

export function useDiagramContextMenu() {
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [contextTargetType, setContextTargetType] = useState<ContextTargetType>(null);
  const [contextTargetNode, setContextTargetNode] = useState<Node<NodeData> | null>(null);
  const [contextTargetEdge, setContextTargetEdge] = useState<Edge | null>(null);

  const handleContextMenuNode: NodeMouseHandler = useCallback((event, node) => {
    event.preventDefault();
    event.stopPropagation();
    
    let targetType: ContextTargetType = "node";
    if (node.type === "labeledGroup") {
      targetType = "group";
    } else if (node.type === "textNode") {
      targetType = "text";
    } else if (node.type === "actorNode") {
      targetType = "actor";
    } else if (node.type === "imageNode") {
      targetType = "image";
    }
    
    setContextTargetType(targetType);
    setContextTargetNode(node as Node<NodeData>);
    setContextTargetEdge(null);
    setContextMenuPosition({ x: event.clientX, y: event.clientY });
  }, []);

  const handleContextMenuEdge: EdgeMouseHandler = useCallback((event, edge) => {
    event.preventDefault();
    event.stopPropagation();
    setContextTargetType("edge");
    setContextTargetEdge(edge);
    setContextTargetNode(null);
    setContextMenuPosition({ x: event.clientX, y: event.clientY });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextTargetType(null);
    setContextTargetNode(null);
    setContextTargetEdge(null);
  }, []);

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextTargetType) {
        closeContextMenu();
      }
    };

    if (contextTargetType) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('contextmenu', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('contextmenu', handleClickOutside);
    };
  }, [contextTargetType, closeContextMenu]);

  return {
    contextMenuPosition,
    contextTargetType,
    contextTargetNode,
    contextTargetEdge,
    handleContextMenuNode,
    handleContextMenuEdge,
    closeContextMenu,
    setContextTargetType,
  };
}
